import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCountry } from "@/contexts/CountryContext";
import { useMember } from "@/components/community/parts";
import { readPlanDraft, savePlanDraft, type PlanDraft } from "@/lib/plan-draft";
import { track } from "@/lib/analytics";
import { CountryStep, useCountryGuess } from "./CountryStep";
import { C, STEPS, StepBars } from "./parts";

/**
 * The plan's first question in the homepage hero. One tap saves the country
 * with the rest of the draft and opens /start at the next question, so there
 * is one plan form (PlanFlow) to improve and one funnel to read. Events carry
 * entry "hero"; /start counts the rest of the screens.
 *
 * Why not the whole form here: the profile card needs the width, and five
 * more screens in the hero push the page down. The old five-question hero card
 * finished 30% of starts, /start 42% (PostHog, October 2026).
 */
const EMPTY: PlanDraft = { step: 0, code: null, start: null, route: null, german: null, english: null, age: null, money: null, email: "", minor: false, parent: "", now: null };

export function PlanLauncher() {
  const navigate = useNavigate();
  const { setAndConfirm } = useCountry();
  const account = useMember()?.email ?? null;
  const [draft] = useState(readPlanDraft);
  const [code, setCode] = useState<string | null>(draft?.code ?? null);
  const { guess, guessing } = useCountryGuess(!!draft?.code);

  // Load /start while they read the hero, so the next question opens at once.
  useEffect(() => {
    const id = window.setTimeout(() => void import("@/pages/Start").catch(() => undefined), 1500);
    return () => window.clearTimeout(id);
  }, []);

  const choose = (c: string) => {
    setCode(c);
    setAndConfirm(c);
    // Keeps any answers already on this device; /start resumes at the first open question.
    const d = readPlanDraft();
    savePlanDraft({ ...EMPTY, ...d, code: c, step: Math.max(d?.step ?? 0, 1) });
    track("plan_form_started", { country: c, form: "v2", entry: "hero" });
    track("plan_step_completed", { step: 1, of: STEPS.length, name: "country", country: c, form: "v2", entry: "hero" });
    // Once the tap has registered, as on /start.
    window.setTimeout(() => navigate("/start", { state: { entry: "hero" } }), 320);
  };

  return (
    <div className="relative rounded-[32px] bg-white p-5 sm:p-8" style={{ boxShadow: "0 1px 2px rgba(10,15,14,0.04), 0 18px 50px rgba(10,15,14,0.07)" }}>
      <div className="flex h-9 items-center gap-3">
        <span className="text-[13px] font-bold" style={{ color: C.muted }}>
          About a minute
        </span>
        <StepBars index={0} total={STEPS.length} />
      </div>
      <h2 className="mt-4 text-[28px] font-black leading-[1.08] tracking-[-0.035em] [text-wrap:balance] sm:text-[32px]" style={{ color: C.ink }}>
        Where are you moving from?
      </h2>
      <CountryStep code={code} guess={guess} guessing={guessing} account={account} onChoose={choose} narrow />
    </div>
  );
}
